import React, { useRef } from "react";
import Button from "../../components/UI/Buttons/Button/Button";
import {
  PendingMediaFile,
  StoredMediaFile,
  mediaTypeOf,
  validateMediaFile,
} from "../../services/mediaPayload";
import styles from "./AdvisorProfilePage.module.scss";

// ITAM-2887: ported from the pharmacist profile page (ITAM-2622, decision 20).
// Post-creation photo/video add, replace and removal. Client pre-validation
// mirrors the backend's own checks (type whitelist, 10 MB image / 50 MB video,
// at most ONE video across kept + new). The FE deliberately does NOT
// compensate for the backend's media write path.

const CDN_BASE = process.env.REACT_APP_CDN_PATH ?? "";

export const mediaUrl = (location: string): string =>
  `${CDN_BASE}${location}`;

interface MediaManagerProps {
  kept: StoredMediaFile[];
  pending: PendingMediaFile[];
  disabled: boolean;
  onKeptChange: (next: StoredMediaFile[]) => void;
  onPendingChange: (next: PendingMediaFile[]) => void;
  error: string | null;
  onError: (message: string | null) => void;
}

const MediaManager: React.FC<MediaManagerProps> = ({
  kept,
  pending,
  disabled,
  onKeptChange,
  onPendingChange,
  error,
  onError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const videoCount =
    kept.filter((row) => row.type === "VIDEO").length +
    pending.filter((item) => item.type === "VIDEO").length;

  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    const accepted: PendingMediaFile[] = [];
    let videos = videoCount;
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const problem = validateMediaFile(file);
      if (problem) {
        onError(problem);
        continue;
      }
      const type = mediaTypeOf(file);
      if (type === "VIDEO") {
        if (videos >= 1) {
          onError("Dozvoljen je najviše jedan video.");
          continue;
        }
        videos += 1;
      }
      accepted.push({ file, type });
    }
    if (accepted.length > 0) {
      onError(null);
      onPendingChange([...pending, ...accepted]);
    }
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <div className={styles.mediaManager}>
      <h3 className={styles.subTitle}>Fotografije i video</h3>
      <p className={styles.hint}>
        Slike do 10 MB (JPEG, PNG, WebP) i najviše jedan video do 50 MB (MP4,
        MOV).
      </p>

      <ul className={styles.mediaGrid}>
        {kept.map((row) => (
          <li key={`kept-${row.id}`} className={styles.mediaItem}>
            {row.type === "VIDEO" ? (
              <video
                className={styles.mediaPreview}
                src={mediaUrl(row.location)}
                controls
              />
            ) : (
              <img
                className={styles.mediaPreview}
                src={mediaUrl(row.location)}
                alt=""
              />
            )}
            <Button
              buttonType="white"
              disabled={disabled}
              onClick={() =>
                onKeptChange(kept.filter((item) => item.id !== row.id))
              }
            >
              Ukloni
            </Button>
          </li>
        ))}
        {pending.map((item, index) => (
          <li
            key={`pending-${index}-${item.file.name}`}
            className={styles.mediaItem}
          >
            <span className={styles.mediaPending}>
              {item.type === "VIDEO" ? "Video" : "Slika"}: {item.file.name}
            </span>
            <Button
              buttonType="white"
              disabled={disabled}
              onClick={() =>
                onPendingChange(pending.filter((_, i) => i !== index))
              }
            >
              Ukloni
            </Button>
          </li>
        ))}
      </ul>

      {error && (
        <p className={styles.fieldError} role="alert">
          {error}
        </p>
      )}

      <input
        ref={inputRef}
        className={styles.fileInput}
        type="file"
        multiple
        accept="image/jpeg,image/jpg,image/png,image/webp,video/mp4,video/quicktime"
        disabled={disabled}
        aria-label="Dodajte fotografije ili video"
        onChange={(e) => addFiles(e.target.files)}
      />
    </div>
  );
};

export default MediaManager;
